import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Calendar, User, TrendingUp, CheckCircle, ExternalLink, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const bankData = [
    { name: 'Banco Agrícola', rate: 7.25, minIncome: 1500, term: 30, requirements: "Ingresos > $1,500, Estabilidad laboral 1 año", link: 'https://www.bancoagricola.com/personas/creditos/vivienda' },
    { name: 'Banco Cuscatlán', rate: 7.50, minIncome: 1800, term: 25, requirements: "Ingresos > $1,800, Buen récord crediticio", link: 'https://www.bancocuscatlan.com/credito-de-vivienda' },
    { name: 'BAC Credomatic', rate: 7.35, minIncome: 1600, term: 30, requirements: "Ingresos > $1,600, Prima 10%", link: 'https://www.baccredomatic.com/es-sv/creditos' },
    { name: 'Banco Promerica', rate: 7.60, minIncome: 1500, term: 25, requirements: "Ingresos > $1,500, Constancia salarial", link: 'https://www.promerica.com.sv/banca-de-personas/creditos/credito-hipotecario/' },
    { name: 'Banco Davivienda', rate: 7.45, minIncome: 1700, term: 30, requirements: "Ingresos > $1,700, Seguro de vida", link: 'https://www.davivienda.com.sv/personas/prestamos/vivienda' },
    { name: 'Banco Hipotecario', rate: 7.25, minIncome: 1300, term: 30, requirements: "Ingresos > $1,300, Sector formal/informal", link: 'https://www.bancohipotecario.com.sv/credito-para-vivienda/' },
    { name: 'FSV (Social)', rate: 6.00, minIncome: 450, term: 30, requirements: "Ingresos > $450, Vivienda de interés social", link: 'https://www.fsv.gob.sv/' },
    { name: 'FSV (Hermanos Lejanos)', rate: 6.50, minIncome: 1000, term: 30, requirements: "Estatus migratorio regular, Envíos de remesas", link: 'https://www.fsv.gob.sv/hermanos-lejanos/' },
    { name: 'Cajas de Crédito (Fedecredito)', rate: 8.50, minIncome: 600, term: 20, requirements: "Ingresos > $600, Comerciantes/Informales", link: 'https://www.fedecredito.com.sv/' },
];

const formatCurrency = (value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const RatesComparison = () => {
    const [loanAmount, setLoanAmount] = useState(100000);
    const [loanTerm, setLoanTerm] = useState(20);
    const [monthlyIncome, setMonthlyIncome] = useState(2000);
    const [results, setResults] = useState([]);

    const handleCalculation = () => {
        const calculatedResults = bankData
            .map(bank => {
                const principal = loanAmount;
                const term = Math.min(loanTerm, bank.term);
                const monthlyRate = bank.rate / 100 / 12;
                const numberOfPayments = term * 12;
                const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

                // Determine eligibility based on income
                const isEligible = monthlyIncome >= bank.minIncome;

                return { ...bank, monthlyPayment, isEligible, effectiveTerm: term };
            })
            .sort((a, b) => a.monthlyPayment - b.monthlyPayment);

        setResults(calculatedResults);
    };

    const bestOption = useMemo(() => {
        const eligibleOptions = results.filter(r => r.isEligible);
        if (eligibleOptions.length > 0) return eligibleOptions[0].name;
        return null;
    }, [results]);

    return (
        <div className="bg-white p-8 rounded-xl shadow-inner space-y-12">
            <section className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                <div>
                    <Label htmlFor="loanAmount" className="text-secondary font-semibold">Monto del Préstamo</Label>
                    <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <Input
                            id="loanAmount"
                            type="number"
                            value={loanAmount}
                            onChange={e => setLoanAmount(e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="100,000"
                            className="pl-10"
                        />
                    </div>
                </div>
                <div>
                    <Label htmlFor="loanTerm" className="text-secondary font-semibold">Plazo (años)</Label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <Input
                            id="loanTerm"
                            type="number"
                            value={loanTerm}
                            onChange={e => setLoanTerm(e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="20"
                            className="pl-10"
                        />
                    </div>
                </div>
                <div>
                    <Label htmlFor="monthlyIncome" className="text-secondary font-semibold">Ingreso Mensual</Label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <Input
                            id="monthlyIncome"
                            type="number"
                            value={monthlyIncome}
                            onChange={e => setMonthlyIncome(e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="2,000"
                            className="pl-10"
                        />
                    </div>
                </div>
                <Button onClick={handleCalculation} className="w-full bg-primary text-white">
                    <TrendingUp className="mr-2 h-4 w-4" /> Comparar
                </Button>
            </section>

            {results.length > 0 && (
                <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <h3 className="text-2xl font-bold text-secondary mb-6">Resultados de la Comparación</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-200 bg-gray-50">
                                    <th className="p-4 font-semibold text-secondary rounded-tl-lg">Entidad</th>
                                    <th className="p-4 font-semibold text-secondary">Tasa Anual</th>
                                    <th className="p-4 font-semibold text-secondary">Cuota Mensual</th>
                                    <th className="p-4 font-semibold text-secondary">Plazo Máx</th>
                                    <th className="p-4 font-semibold text-secondary">Requisitos</th>
                                    <th className="p-4 font-semibold text-secondary rounded-tr-lg">Contacto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((bank) => (
                                    <motion.tr
                                        key={bank.name}
                                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${bank.name === bestOption ? 'bg-green-50 hover:bg-green-100' : ''} ${!bank.isEligible ? 'opacity-60' : ''}`}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <td className="p-4 font-medium text-secondary flex items-center">
                                            {bank.name}
                                            {bank.name === bestOption && <CheckCircle className="ml-2 h-5 w-5 text-green-600" />}
                                            {!bank.isEligible && <span className="ml-2 text-xs text-red-500 font-normal">(Ingresos insuficientes)</span>}
                                        </td>
                                        <td className="p-4 text-primary font-bold">{bank.rate.toFixed(2)}%</td>
                                        <td className="p-4 font-semibold text-lg text-secondary">{formatCurrency(bank.monthlyPayment)}</td>
                                        <td className="p-4 text-gray-600">{bank.term} años</td>
                                        <td className="p-4 text-sm text-gray-600 max-w-xs">
                                            <div className="flex items-start gap-2">
                                                <FileText className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                                                {bank.requirements}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <a href={bank.link} target="_blank" rel="noopener noreferrer">
                                                <Button variant="outline" size="sm" className="text-primary hover:bg-primary hover:text-white border-primary w-full">
                                                    <ExternalLink className="h-4 w-4 mr-2" /> Ver Más
                                                </Button>
                                            </a>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-bold">Mejor Opción Recomendada:</p>
                            <p>Basado en tu perfil, <strong>{bestOption || "Ninguna opción disponible"}</strong> parece ofrecer la cuota mensual más baja entre las opciones para las que calificas.</p>
                        </div>
                    </div>
                </motion.section>
            )}
            <div className="mt-8 text-xs text-gray-500 text-center">
                <strong>Nota:</strong> Las tasas y condiciones son de referencia y pueden cambiar. La elegibilidad final depende de la evaluación de cada entidad financiera.
            </div>
        </div>
    );
};

export default RatesComparison;
